import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import {
  ApiFilterOperation,
  TableCellProps,
  TableCells,
  TableColumn,
  TableFilterCells,
  eqFilterParams,
  useStaticListSource,
} from '@eas/common-web';
import { useJudges } from '@modules/judge/judge-api';
import { useUserRoles } from '@modules/role/role-api';
import { useCustomFilterAutocomplete } from '@composite/autocomplete-item/autocomplete-item-with-deactivated-items';
import { Redirect } from '@composite/redirect/redirect';
import { StaticDataContext } from '@components/evidence/static-data-provider/static-data-provider';
import { autocompleteColumnMapper } from '@components/form/misc/label-mappers';
import { Deadline } from '@models';
import { DocumentType, EvidenceBrowserUrl, Role } from '@enums';

function getDocumentUrl(type: DocumentType) {
  switch (type) {
    case DocumentType.DELIVERED:
      return EvidenceBrowserUrl.DOCUMENTS_DELIVERED_PROCESSED;
      break;
    case DocumentType.INTERNAL:
      return EvidenceBrowserUrl.DOCUMENTS_INTERNAL;
      break;
    case DocumentType.OUTGOING:
      return EvidenceBrowserUrl.DOCUMENTS_OUTGOING;
      break;

    default:
      return EvidenceBrowserUrl.DOCUMENTS_DELIVERED_PROCESSED;
      break;
  }
}

export function useColumns(): TableColumn<Deadline>[] {
  const intl = useIntl();

  const { deadlineStates } = useContext(StaticDataContext);
  const useDeadlineStates = () => useStaticListSource(deadlineStates ?? []);

  const useSecretaries = () =>
    useUserRoles({
      ...eqFilterParams({ field: 'role.id', value: Role.SECRETARY }),
      include: ['DEACTIVATED', 'DELETED'],
    });

  const useIndexes = () =>
    useUserRoles({
      ...eqFilterParams({ field: 'role.id', value: Role.INDEX }),
      include: ['DEACTIVATED', 'DELETED'],
    });

  return [
    {
      datakey: 'id',
      name: '',
      width: 80,
      CellComponent: function Cell(props: TableCellProps<Deadline>) {
        const url = getDocumentUrl(props.rowValue.document!.type!);

        return (
          <>
            <Redirect url={url} id={props.rowValue.document!.id} />
          </>
        );
      },
    },
    {
      name: intl.formatMessage({
        id: 'ES__DEADLINES__ES_FILE_NUMBER__LABEL',
        defaultMessage: 'Spisová značka',
      }),
      datakey: 'document.esFile.number.value',
      sortkey: 'document.esFile.number.valueSort',
      CellComponent: TableCells.TextCell,
      width: 120,
      sortable: true,
      filterable: true,
    },
    {
      name: intl.formatMessage({
        id: 'ES__DEADLINES__ES_FILE_JUDGE__LABEL',
        defaultMessage: 'Source zpravodaj',
      }),
      datakey: 'document.esFile.judge',
      sortkey: 'document.esFile.judge.name',
      filterkey: 'document.esFile.judge.id',
      valueMapper: autocompleteColumnMapper,
      CellComponent: TableCells.TextCell,
      FilterComponent: useCustomFilterAutocomplete({
        useSource: () => useJudges({ include: ['DEACTIVATED'] }),
      }),
      filterOperation: ApiFilterOperation.EQ,
      width: 150,
      sortable: true,
      filterable: true,
    },
    {
      name: intl.formatMessage({
        id: 'ES__DEADLINES__STATE__LABEL',
        defaultMessage: 'Stav',
      }),
      datakey: 'state',
      sortkey: 'state.name',
      filterkey: 'state.id',
      width: 100,
      valueMapper: TableCells.useSelectCellFactory(useDeadlineStates),
      CellComponent: TableCells.TextCell,
      FilterComponent:
        TableFilterCells.useFilterSelectCellFactory(useDeadlineStates),
      filterOperation: ApiFilterOperation.EQ,
      sortable: true,
      filterable: true,
    },
    {
      name: intl.formatMessage({
        id: 'ES__DEADLINES__DAYS_REMAINING__LABEL',
        defaultMessage: 'Zbývá',
      }),
      datakey: 'daysRemaining',
      sortkey: 'daysRemaining',
      width: 60,
      CellComponent: TableCells.NumberCell,
      FilterComponent: TableFilterCells.FilterNumberCell,
      sortable: true,
      filterable: true,
    },
    {
      name: intl.formatMessage({
        id: 'ES__DEADLINES__STARTING_FROM__LABEL',
        defaultMessage: 'Počáteční datum',
      }),
      datakey: 'startingFrom',
      sortkey: 'startingFrom',
      width: 120,
      CellComponent: TableCells.DateCell,
      FilterComponent: TableFilterCells.FilterDateCell,
      sortable: true,
      filterable: true,
    },
    {
      name: intl.formatMessage({
        id: 'ES__DEADLINES__END_DATE__LABEL',
        defaultMessage: 'Konečné datum',
      }),
      datakey: 'endDate',
      sortkey: 'endDate',
      width: 120,
      CellComponent: TableCells.DateCell,
      FilterComponent: TableFilterCells.FilterDateCell,
      sortable: true,
      filterable: true,
    },
    {
      name: intl.formatMessage({
        id: 'ES__DEADLINES__DURATION__LABEL',
        defaultMessage: 'Počet dnů',
      }),
      datakey: 'duration',
      sortkey: 'duration',
      CellComponent: TableCells.NumberCell,
      FilterComponent: TableFilterCells.FilterNumberCell,
      width: 120,
      sortable: true,
      filterable: true,
    },
    {
      name: intl.formatMessage({
        id: 'ES__DEADLINES__REFERENCE_NUMBER__LABEL',
        defaultMessage: 'Číslo jednací',
      }),
      datakey: 'document.referenceNumber',
      sortkey: 'document.referenceNumberSort',
      CellComponent: TableCells.TextCell,
      width: 120,
      sortable: true,
      filterable: true,
    },
    {
      name: intl.formatMessage({
        id: 'ES__DEADLINES_TOOLBAR__COLUMN__DESCRIPTION',
        defaultMessage: 'Popis',
      }),
      datakey: 'description',
      sortkey: 'description',
      CellComponent: TableCells.TextCell,
      width: 200,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'document.esFile.indexUserRole',
      filterkey: 'document.esFile.indexUserRole.id',
      sortkey: 'document.esFile.indexUserRole.name',
      name: intl.formatMessage({
        id: 'ES__DOCUMENT_DISPATCHES__TABLE__COLUMN__INDEX_USER_ROLE',
        defaultMessage: 'Rejstřík',
      }),
      width: 200,
      valueMapper: autocompleteColumnMapper,
      CellComponent: TableCells.TextCell,
      FilterComponent: useCustomFilterAutocomplete({
        useSource: useIndexes,
      }),

      filterOperation: ApiFilterOperation.EQ,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'document.esFile.secretaryUserRole',
      filterkey: 'document.esFile.secretaryUserRole.id',
      sortkey: 'document.esFile.secretaryUserRole.name',
      name: intl.formatMessage({
        id: 'ES__DEADLINES__SECRETARY__LABEL',
        defaultMessage: 'Tajemnice',
      }),
      width: 150,
      valueMapper: autocompleteColumnMapper,
      CellComponent: TableCells.TextCell,
      FilterComponent: useCustomFilterAutocomplete({
        useSource: useSecretaries,
      }),
      filterOperation: ApiFilterOperation.EQ,
      sortable: true,
      filterable: true,
    },
  ];
}
